import React, { useCallback, useEffect, useRef } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, { runOnJS } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';

import { Box } from '@/components/lib/components';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CircularPager } from '@/feature/circular-pager/CircularPager';
import { getRealIndex, moddedIndex, moveToClosestIndex } from '@/feature/circular-pager/circular-helpers';
import { CircularPagerImperativeApi, CircularPagerPageProps } from '@/feature/circular-pager/types';
import {
    PlayerProfileSheet,
    PlayerProfileSheetScreen,
} from '@/feature/player-profile-sheets/components/PlayerProfileSheetScreen';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { useLiveEventsPropsUpdate } from '../betslip-pickem/hooks/use-live-props-update';
import {
    FantasyFullScreenPagerTransitionProvider,
    useFantasyFullScreenPagerTransition,
} from '../circular-pager/FantasyFullScreenPagerTransitionProvider';
import { ScrollableModalContextProvider } from '../circular-pager/ScrollableModalContextProvider';
import { SheetBackground } from '../circular-pager/SheetBackground';
import { AnimatedPickBar } from '../lobby/components/Pickbar';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { PlayersCarouselTabBar } from './PlayersCarouselTabBar';
import { usePlayerProfileDataStore } from './hooks/use-player-profile-data-store';

const PAGE_GAP = 20;

export type PlayerProfileSheetsParamsList = StackScreenProps<RootStackParamList, 'PlayerProfileSheets'>;

export const PlayerProfileSheetsScreen = () => {
    const { params } = useRoute<PlayerProfileSheetsParamsList['route']>();
    const selectedPlayerIndex = usePlayerProfileDataStore(state => state.selectedPlayerIndex);
    useFocusEffect(
        useCallback(() => {
            BetrAnalytics.trackScreenView('PlayerProfileSheets');
        }, [])
    );
    return (
        <FantasyFullScreenPagerTransitionProvider key={params.contextKey} initialIndex={selectedPlayerIndex}>
            <PlayerProfileSheetsTabs />
        </FantasyFullScreenPagerTransitionProvider>
    );
};

export const PLAYER_CAROUSEL_TAB_BAR_HEIGHT = 64;

const PlayerProfileSheetsTabs = () => {
    const { height, width } = useWindowDimensions();
    const listOfPlayers = usePlayerProfileDataStore(state => state.playersList);

    useLiveEventsPropsUpdate({
        //create a map of unique events from the list of players and then convert it to an array
        events: Array.from(new Map(listOfPlayers.map(it => [it.event.id, it.event])).values()),
        options: {
            subscribeToGameClockUpdates: true,
            subscribeToMarketUpdates: true,
            debugTag: 'PlayerProfileSheetsTabs',
        },
    });

    const { selectedTabSharedValue, pageAnim } = useFantasyFullScreenPagerTransition();
    const insets = useSafeAreaInsets();
    const pagerRef = useRef<CircularPagerImperativeApi>(null);
    const setSelectedPlayerIndex = usePlayerProfileDataStore(state => state.actions.setSelectedPlayerIndex);
    const selectedPlayerIndex = usePlayerProfileDataStore(state => state.selectedPlayerIndex);

    const internalTab = useRef(selectedPlayerIndex);
    const totalPages = listOfPlayers?.length || 0;

    useEffect(() => {
        if (internalTab.current !== selectedPlayerIndex && totalPages > 0) {
            internalTab.current = selectedPlayerIndex;
            selectedTabSharedValue.value = selectedPlayerIndex;
            pagerRef.current?.setPage(moveToClosestIndex(pageAnim.value, selectedPlayerIndex, totalPages), {
                animated: true,
            });
        }
    }, [selectedPlayerIndex, totalPages, selectedTabSharedValue, pageAnim]);

    const onlyOnePage = totalPages === 1;

    const handlePageChange = useCallback(
        (rawIndex: number) => {
            const realIndex = totalPages === 0 ? rawIndex : listOfPlayers ? moddedIndex(rawIndex, totalPages) : 0;
            runOnJS(() => {
                selectedTabSharedValue.value = realIndex;
                internalTab.current = realIndex;
                setSelectedPlayerIndex(realIndex);
            })();
        },
        [setSelectedPlayerIndex, selectedTabSharedValue, listOfPlayers, totalPages]
    );
    const navigation = useNavigation<StackNavigationProp<RootStackParamList, 'PlayerProfileSheets'>>();

    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const renderPage = useCallback(
        ({ offset, currentIndex, shouldFreeze, isActive }: CircularPagerPageProps) => {
            if (!listOfPlayers) {
                return null;
            }
            const realIndex = getRealIndex(offset, currentIndex, totalPages);
            const playerInfo = listOfPlayers[realIndex];
            return <PlayerProfileSheet shouldFreeze={shouldFreeze} isActive={isActive} playerInfo={playerInfo} />;
        },
        [listOfPlayers, totalPages]
    );

    const renderWebPage = useCallback(() => {
        const playerInfo = listOfPlayers[selectedPlayerIndex];

        return (
            <MaxWidthWrapper flex={1}>
                <ScrollableModalContextProvider>
                    <PlayerProfileSheetScreen isActive={true} playerInfo={playerInfo} />
                </ScrollableModalContextProvider>
            </MaxWidthWrapper>
        );
    }, [listOfPlayers, selectedPlayerIndex]);

    if (totalPages === 0) {
        return null;
    }
    const validInitialTab = Math.min(Math.max(selectedPlayerIndex, 0), totalPages - 1);

    const positionStyle = isWeb ? undefined : common.absolute;

    return (
        <Box width={width} height={'100%'}>
            <SheetBackground />
            {onlyOnePage ? null : (
                <MaxWidthWrapper>
                    <PlayersCarouselTabBar />
                </MaxWidthWrapper>
            )}
            <Animated.View
                style={[
                    {
                        height,
                        top: insets.top,
                        width,
                        backgroundColor: isWeb ? designSystem.colors.gray8 : undefined,
                    },
                    positionStyle,
                ]}
            >
                <CircularPager
                    ref={pagerRef}
                    pageCallbackNode={pageAnim}
                    initialIndex={validInitialTab}
                    onPageChange={handlePageChange}
                    pageGap={PAGE_GAP}
                    renderPage={isWeb ? renderWebPage : renderPage}
                    aboveSheetHeaderHeight={PLAYER_CAROUSEL_TAB_BAR_HEIGHT}
                    onClose={onClose}
                    onlyOnePage={onlyOnePage}
                />
            </Animated.View>
            <AnimatedPickBar />
        </Box>
    );
};
